import { useEffect, useRef } from 'react';

/**
 * Catches the browser's "back" gesture (Android system back / swipe-back in PWA,
 * desktop browser back button, etc.) while `active` is true and calls `onBack`
 * instead of letting the browser navigate away.
 *
 * Works by pushing a sentinel history entry when `active` becomes true. When the
 * user presses back, the browser pops that entry → popstate fires → we call
 * `onBack()`. This is the standard pattern for keeping a PWA from exiting when
 * the user just wants to close a panel/modal.
 *
 * Usage:
 *   useBackGuard(isOpen, () => setIsOpen(false));
 */
export default function useBackGuard(active, onBack) {
    const cbRef = useRef(onBack);
    useEffect(() => { cbRef.current = onBack; }, [onBack]);

    useEffect(() => {
        if (!active) return;

        // Defer the pushState by one tick so React StrictMode's immediate
        // mount → cleanup → mount cycle doesn't leave a stray sentinel that
        // the second mount's handler would then catch and treat as a real
        // back press (which was previously causing modals to auto-close).
        const sentinelId = `back-guard-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        let pushed = false;
        let consumedByPop = false;

        const timer = setTimeout(() => {
            window.history.pushState({ backGuard: sentinelId }, '');
            pushed = true;
        }, 0);

        const handler = () => {
            // Ignore stray popstates that fire before our sentinel is on top —
            // they belong to a sibling guard's cleanup (StrictMode race).
            if (!pushed) return;
            consumedByPop = true;
            cbRef.current?.();
        };

        window.addEventListener('popstate', handler);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('popstate', handler);
            // Only pop our sentinel if we actually pushed it and the user
            // closed via UI (not via a real back gesture).
            if (pushed && !consumedByPop && window.history.state?.backGuard === sentinelId) {
                window.history.back();
            }
        };
    }, [active]);
}
